import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../../../config/AuthContext';
import {
    Button,
    Space,
    Typography,
    Tooltip,
    Select,
    Input,
    Tag,
    Modal,
    message,
    Card,
    Alert,
    Divider,
    Radio,
    Table as AntdTable,
    Checkbox,
    Drawer
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
    KeyOutlined,
    LinkOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    EditOutlined,
    SettingOutlined
} from '@ant-design/icons';
import {
    RelationalDiagramData,
    RelationalTable,
    RelationalColumn,
    RelationalForeignKey
} from './model/ErModel';
import {
    validateRelationalDiagram,
    RelationalValidationResult
} from './engine/RelationalValidator';

const { Text, Title } = Typography;
const { Option } = Select;

const SQL_TYPES = [
    'INT',
    'BIGINT',
    'VARCHAR(50)',
    'VARCHAR(100)',
    'VARCHAR(255)',
    'DECIMAL(10,2)',
    'DATE',
    'DATETIME',
    'TEXT',
    'BOOLEAN',
    'BLOB'
];

interface RelationalDiagramIdeProps {
    initialData?: RelationalDiagramData | string | null;
    solutionData?: RelationalDiagramData | string | null;
    readOnly?: boolean;
    isTeacherMode?: boolean;
    hideImportExport?: boolean;
    hideSave?: boolean;
    hideEvaluate?: boolean;
    onSave?: (data: RelationalDiagramData) => void;
    onManualSave?: (data: RelationalDiagramData) => void | Promise<void>;
    onSubmitEvaluation?: (data: RelationalDiagramData, result: RelationalValidationResult) => void;
    customConfig?: any;
    intentsFallitsPrevis?: number;
    title?: string;
    onBack?: () => void;
}

export const RelationalDiagramIde: React.FC<RelationalDiagramIdeProps> = ({
    initialData,
    solutionData,
    readOnly = false,
    isTeacherMode = false,
    hideImportExport = false,
    hideSave = false,
    hideEvaluate = false,
    onSave,
    onManualSave,
    onSubmitEvaluation,
    customConfig,
    intentsFallitsPrevis = 0,
    title = 'IDE de Disseny: Model Relacional (Taules)',
    onBack
}) => {
    const [diagram, setDiagram] = useState<RelationalDiagramData>(() => {
        if (!initialData) return { type: 'Relacional', version: 1, tables: [], foreignKeys: [] };
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && (parsed.type === 'Relacional' || parsed.tables)) return parsed;
            } catch (e) { }
            return { type: 'Relacional', version: 1, tables: [], foreignKeys: [] };
        }
        return initialData;
    });

    const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
    const [fkSource, setFkSource] = useState<{ table: string; column: string } | null>(null);
    const [zoom, setZoom] = useState<number>(1);
    const [draggingTableId, setDraggingTableId] = useState<string | null>(null);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const [newTableModalVisible, setNewTableModalVisible] = useState(false);
    const [newTableName, setNewTableName] = useState('');

    const [evalResult, setEvalResult] = useState<RelationalValidationResult | null>(null);
    const [evalModalVisible, setEvalModalVisible] = useState(false);
    const [saving, setSaving] = useState(false);

    const svgRef = useRef<SVGSVGElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Sincronitzar amb initialData quan canvia la prop (per exemple, en carregar dades d'enviament de l'alumne)
    const isFirstMountRef = useRef(true);
    useEffect(() => {
        if (!initialData) return;
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && (parsed.type === 'Relacional' || parsed.tables)) {
                    setDiagram(parsed);
                }
            } catch (e) { }
        } else if (initialData && ((initialData as any).type === 'Relacional' || (initialData as any).tables)) {
            setDiagram(initialData);
        }
    }, [initialData]);

    // Auto-save debounced (només quan l'usuari ha interactuat, no en el primer render)
    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }
        if (onSave) {
            const timer = setTimeout(() => {
                onSave(diagram);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [diagram, onSave]);

    const TABLE_WIDTH = 220;
    const HEADER_HEIGHT = 38;
    const ROW_HEIGHT = 28;

    const getTableDimensions = (table: RelationalTable) => {
        const height = HEADER_HEIGHT + Math.max(1, table.columns.length) * ROW_HEIGHT + 10;
        return { width: TABLE_WIDTH, height };
    };

    // Coordenades centrals per a les fletxes de FK
    const getColumnCenter = (tableName: string, colName: string): { x: number; y: number } => {
        const table = diagram.tables.find(t => t.name === tableName);
        if (!table) return { x: 0, y: 0 };

        const colIndex = table.columns.findIndex(c => c.name === colName);
        const yOffset = HEADER_HEIGHT + (colIndex >= 0 ? colIndex * ROW_HEIGHT : 0) + ROW_HEIGHT / 2;

        return {
            x: table.x + TABLE_WIDTH,
            y: table.y + yOffset
        };
    };

    const getTableLeftCenter = (tableName: string): { x: number; y: number } => {
        const table = diagram.tables.find(t => t.name === tableName);
        if (!table) return { x: 0, y: 0 };
        const dims = getTableDimensions(table);
        return {
            x: table.x,
            y: table.y + dims.height / 2
        };
    };

    // Drag & drop de taules
    const handleMouseDown = (tableId: string, e: React.MouseEvent) => {
        if (readOnly) return;
        e.stopPropagation();
        setDraggingTableId(tableId);
        const table = diagram.tables.find(t => t.id === tableId);
        if (table) {
            const rect = svgRef.current?.getBoundingClientRect();
            const startMouseX = rect ? (e.clientX - rect.left) / zoom : e.clientX / zoom;
            const startMouseY = rect ? (e.clientY - rect.top) / zoom : e.clientY / zoom;
            setDragOffset({
                x: startMouseX - table.x,
                y: startMouseY - table.y
            });
        }
        setSelectedTableId(tableId);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!draggingTableId || readOnly) return;
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = (e.clientX - rect.left) / zoom;
        const mouseY = (e.clientY - rect.top) / zoom;

        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === draggingTableId) {
                    return {
                        ...t,
                        x: Math.max(10, Math.round(mouseX - dragOffset.x)),
                        y: Math.max(10, Math.round(mouseY - dragOffset.y))
                    };
                }
                return t;
            })
        }));
    };

    const handleMouseUp = () => {
        setDraggingTableId(null);
    };

    // Afegir taula (inicialment buida sense columnes ni PK predeterminades)
    const handleAddTable = () => {
        if (!newTableName.trim()) return;
        const name = newTableName.trim().toUpperCase();
        const newTable: RelationalTable = {
            id: `tbl_${Date.now()}`,
            name,
            x: 80 + (diagram.tables.length % 3) * 260,
            y: 80 + Math.floor(diagram.tables.length / 3) * 220,
            columns: []
        };
        setDiagram(prev => ({ ...prev, tables: [...prev.tables, newTable] }));
        setSelectedTableId(newTable.id);
        setNewTableName('');
        setNewTableModalVisible(false);
    };

    // Eliminar taula
    const handleRemoveTable = (tableId: string) => {
        const table = diagram.tables.find(t => t.id === tableId);
        if (!table) return;

        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.filter(t => t.id !== tableId),
            foreignKeys: prev.foreignKeys.filter(f => f.fromTable !== table.name && f.toTable !== table.name)
        }));
        if (selectedTableId === tableId) setSelectedTableId(null);
    };

    // Afegir columna
    const handleAddColumn = (tableId: string) => {
        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === tableId) {
                    const colCount = t.columns.length + 1;
                    return {
                        ...t,
                        columns: [
                            ...t.columns,
                            { id: `col_${Date.now()}`, name: `camp_${colCount}`, type: 'VARCHAR(50)', isPK: false }
                        ]
                    };
                }
                return t;
            })
        }));
    };

    // Sessió d'usuari per determinar permisos
    const { user } = useContext(AuthContext);
    const isTeacherOrAdmin = user?.tipus === '0' || user?.tipus === '1' || isTeacherMode;

    // Estat per a edició de columnes
    const [columnDrawerOpen, setColumnDrawerOpen] = useState(false);

    // Toggle PK d'una columna
    const toggleColumnPk = (tableId: string, colId: string) => {
        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === tableId) {
                    return {
                        ...t,
                        columns: t.columns.map(c => c.id === colId ? { ...c, isPK: !c.isPK } : c)
                    };
                }
                return t;
            })
        }));
    };

    // Actualitzar nom de columna
    const handleUpdateColumnName = (tableId: string, colId: string, newName: string) => {
        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === tableId) {
                    return {
                        ...t,
                        columns: t.columns.map(c => c.id === colId ? { ...c, name: newName } : c)
                    };
                }
                return t;
            })
        }));
    };

    // Actualitzar tipus SQL de columna
    const handleUpdateColumnType = (tableId: string, colId: string, newType: string) => {
        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === tableId) {
                    return {
                        ...t,
                        columns: t.columns.map(c => c.id === colId ? { ...c, type: newType } : c)
                    };
                }
                return t;
            })
        }));
    };

    // Eliminar columna
    const handleRemoveColumn = (tableId: string, colId: string) => {
        setDiagram(prev => ({
            ...prev,
            tables: prev.tables.map(t => {
                if (t.id === tableId) {
                    return {
                        ...t,
                        columns: t.columns.filter(c => c.id !== colId)
                    };
                }
                return t;
            })
        }));
    };

    // Connexió de clau forana (FK) clicant de columna origen a taula destí
    const handleColumnClickForFk = (tableName: string, colName: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (readOnly) return;

        if (!fkSource) {
            setFkSource({ table: tableName, column: colName });
            message.info(`Columna FK "${tableName}.${colName}" seleccionada. Ara clica sobre la taula o clau primària de destí.`);
        } else {
            if (fkSource.table === tableName && fkSource.column === colName) {
                setFkSource(null);
                return;
            }

            // Destí trobat
            const targetTable = diagram.tables.find(t => t.name === tableName);
            const targetPkCol = targetTable?.columns.find(c => c.isPK)?.name || colName;

            const newFk: RelationalForeignKey = {
                id: `fk_${Date.now()}`,
                fromTable: fkSource.table,
                fromColumn: fkSource.column,
                toTable: tableName,
                toColumn: targetPkCol
            };

            setDiagram(prev => ({
                ...prev,
                foreignKeys: [...prev.foreignKeys.filter(f => !(f.fromTable === newFk.fromTable && f.fromColumn === newFk.fromColumn)), newFk]
            }));

            message.success(`Relació FK connectada: ${fkSource.table}.${fkSource.column} → ${tableName}.${targetPkCol}`);
            setFkSource(null);
        }
    };

    // Avaluar contra solució
    const handleEvaluate = () => {
        let sol: RelationalDiagramData | null = null;
        if (typeof solutionData === 'string') {
            try {
                sol = JSON.parse(solutionData);
            } catch (e) { }
        } else if (solutionData) {
            sol = solutionData;
        }

        if (!sol || !sol.tables || sol.tables.length === 0) {
            message.warning("No hi ha una solució vàlida configurada pel professor.");
            return;
        }

        const res = validateRelationalDiagram(diagram, sol, customConfig, intentsFallitsPrevis);
        setEvalResult(res);
        setEvalModalVisible(true);

        if (onSubmitEvaluation) {
            onSubmitEvaluation(diagram, res);
        }
    };

    // Exportar JSON
    const handleExportJson = () => {
        const blob = new Blob([JSON.stringify(diagram, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `model_relacional_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('Model Relacional exportat en JSON!');
    };

    // Importar JSON
    const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parsed = JSON.parse(evt.target?.result as string);
                if (parsed && (parsed.type === 'Relacional' || parsed.tables)) {
                    setDiagram(parsed);
                    message.success('Diagrama Relacional importat!');
                }
            } catch (err) {
                message.error('Error al carregar el fitxer JSON');
            }
        };
        reader.readAsText(file);
    };

    const selectedTable = diagram.tables.find(t => t.id === selectedTableId);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', background: '#f5f7fa', overflow: 'hidden' }}>
            {/* Capçalera */}
            <div style={{
                background: '#1f2430',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#fff',
                borderBottom: '1px solid #333'
            }}>
                <Space size="middle">
                    {onBack && (
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
                            onClick={onBack}
                            style={{ color: '#fff' }}
                        >
                            Tornar
                        </Button>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1890ff' }}>
                        {title}
                    </span>
                    {isTeacherMode && <Tag color="gold">Mode Solució Professor</Tag>}
                </Space>

                <Space size="small">
                    <Tooltip title="Apropar">
                        <Button size="small" icon={<ZoomInOutlined />} onClick={() => setZoom(z => Math.min(2, z + 0.1))} />
                    </Tooltip>
                    <Tooltip title="Allunyar">
                        <Button size="small" icon={<ZoomOutOutlined />} onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} />
                    </Tooltip>

                    {isTeacherOrAdmin && !hideImportExport && (
                        <>
                            <Divider type="vertical" style={{ borderColor: '#555' }} />

                            <Tooltip title="Exportar a JSON">
                                <Button size="small" icon={<DownloadOutlined />} onClick={handleExportJson}>
                                    Exportar JSON
                                </Button>
                            </Tooltip>
                            <label style={{ margin: 0 }}>
                                <Tooltip title="Importar fitxer JSON">
                                    <span className="ant-btn ant-btn-sm ant-btn-default" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <UploadOutlined style={{ marginRight: 6 }} /> Importar
                                    </span>
                                </Tooltip>
                                <input type="file" style={{ display: 'none' }} accept=".json" onChange={handleImportJson} />
                            </label>
                        </>
                    )}

                    {!readOnly && !hideSave && (
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="small"
                            loading={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    if (onManualSave) {
                                        await onManualSave(diagram);
                                    } else {
                                        onSave?.(diagram);
                                        message.success('Diagrama Relacional desat!');
                                    }
                                } catch (e: any) {
                                    message.error('Error en desar: ' + (e?.message || e));
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            Desar
                        </Button>
                    )}

                    {!hideEvaluate && !isTeacherMode && solutionData && (
                        <Button
                            type="primary"
                            style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 600 }}
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={handleEvaluate}
                        >
                            Avaluar & Validar
                        </Button>
                    )}
                </Space>
            </div>

            {/* Cos Principal: Paleta Lateral Esquerra + Llenç Gràfic SVG */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
                {/* Menú Lateral Esquerre per a Taules Relacionals */}
                {!readOnly && (
                    <div style={{
                        width: 230,
                        background: '#ffffff',
                        borderRight: '1px solid #e8e8e8',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '2px 0 8px rgba(0,0,0,0.03)',
                        zIndex: 10,
                        overflowY: 'auto'
                    }}>
                        <div style={{
                            padding: '12px 14px',
                            background: '#fafafa',
                            borderBottom: '1px solid #f0f0f0',
                            fontWeight: 700,
                            fontSize: 12,
                            color: '#434343',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Paleta Relacional
                        </div>

                        <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <Button
                                block
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setNewTableModalVisible(true)}
                                style={{ background: '#1890ff', fontWeight: 600 }}
                            >
                                + Nova Taula
                            </Button>

                            {/* Estat de FK */}
                            {fkSource ? (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Enllaçant Clau Forana"
                                    description={
                                        <div>
                                            <span>Origen: <strong>{fkSource.table}.{fkSource.column}</strong></span>
                                            <div style={{ marginTop: 4 }}>Clica a la taula de destí.</div>
                                            <Button size="small" type="link" onClick={() => setFkSource(null)} style={{ padding: 0 }}>
                                                Cancel·lar
                                            </Button>
                                        </div>
                                    }
                                />
                            ) : (
                                <div style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', padding: 8, borderRadius: 4, border: '1px solid #e2e8f0' }}>
                                    💡 Per enllaçar una <strong>Clau Forana (FK)</strong>: fes clic a la icona 🔗 al costat del camp i després selecciona la taula destí.
                                </div>
                            )}

                            {/* Informació de taula seleccionada */}
                            {selectedTable ? (
                                <Card
                                    size="small"
                                    title={<span style={{ fontSize: 12, fontWeight: 700 }}>Taula: {selectedTable.name}</span>}
                                    style={{ borderColor: '#1890ff', background: '#f0f7ff' }}
                                    bodyStyle={{ padding: 8 }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <Button
                                            block
                                            size="small"
                                            type="primary"
                                            icon={<SettingOutlined />}
                                            onClick={() => setColumnDrawerOpen(true)}
                                            style={{ background: '#0958d9', borderColor: '#0958d9', fontWeight: 600 }}
                                        >
                                            Editar Columnes & Tipus
                                        </Button>
                                        <Button
                                            block
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => handleAddColumn(selectedTable.id)}
                                        >
                                            Afegir Columna Ràpida
                                        </Button>
                                        <Button
                                            block
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemoveTable(selectedTable.id)}
                                        >
                                            Eliminar Taula
                                        </Button>
                                    </div>
                                </Card>
                            ) : (
                                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 10 }}>
                                    Selecciona una taula per editar o modificar les seves columnes i claus.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Llenç SVG */}
                <div style={{ flex: 1, position: 'relative', overflow: 'auto', background: '#fafbfc' }}>
                    <svg
                        ref={svgRef}
                        width={2400}
                        height={1800}
                    style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: '0 0'
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onClick={() => setSelectedTableId(null)}
                >
                    <defs>
                        <pattern id="relGrid" width="30" height="30" patternUnits="userSpaceOnUse">
                            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#f0f0f0" strokeWidth="1" />
                        </pattern>
                        {/* Fletxa per a les claus foranes */}
                        <marker
                            id="fkArrow"
                            viewBox="0 0 10 10"
                            refX="9"
                            refY="5"
                            markerWidth="6"
                            markerHeight="6"
                            orient="auto-start-reverse"
                        >
                            <path d="M 0 1 L 10 5 L 0 9 z" fill="#1890ff" />
                        </marker>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#relGrid)" />

                    {/* Fletxes de Claus Foranes (FK) */}
                    {diagram.foreignKeys.map(fk => {
                        const fromPos = getColumnCenter(fk.fromTable, fk.fromColumn);
                        const toPos = getTableLeftCenter(fk.toTable);

                        // Corba suau de Bezier
                        const dx = Math.max(40, (toPos.x - fromPos.x) / 2);
                        const pathD = `M ${fromPos.x} ${fromPos.y} C ${fromPos.x + dx} ${fromPos.y}, ${toPos.x - dx} ${toPos.y}, ${toPos.x} ${toPos.y}`;

                        return (
                            <g key={fk.id} style={{ cursor: 'pointer' }}>
                                <path
                                    d={pathD}
                                    fill="none"
                                    stroke="#1890ff"
                                    strokeWidth={2}
                                    markerEnd="url(#fkArrow)"
                                />
                                <text
                                    x={(fromPos.x + toPos.x) / 2}
                                    y={(fromPos.y + toPos.y) / 2 - 8}
                                    fontSize={10}
                                    fill="#096dd9"
                                    textAnchor="middle"
                                    fontWeight="bold"
                                >
                                    FK: {fk.fromColumn}
                                </text>
                            </g>
                        );
                    })}

                    {/* Targetes de Taules */}
                    {diagram.tables.map(table => {
                        const dims = getTableDimensions(table);
                        const isSelected = selectedTableId === table.id;

                        return (
                            <g
                                key={table.id}
                                transform={`translate(${table.x}, ${table.y})`}
                                onMouseDown={(e) => handleMouseDown(table.id, e)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTableId(table.id);
                                }}
                                style={{ cursor: 'move' }}
                            >
                                {/* Fons i vora de la taula */}
                                <rect
                                    width={dims.width}
                                    height={dims.height}
                                    rx={6}
                                    fill="#ffffff"
                                    stroke={isSelected ? '#1890ff' : '#cbd5e1'}
                                    strokeWidth={isSelected ? 2.5 : 1.5}
                                    style={{ filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.06))' }}
                                />

                                {/* Capçalera de la taula */}
                                <path
                                    d={`M 0 6 Q 0 0 6 0 L ${dims.width - 6} 0 Q ${dims.width} 0 ${dims.width} 6 L ${dims.width} ${HEADER_HEIGHT} L 0 ${HEADER_HEIGHT} Z`}
                                    fill={isSelected ? '#1890ff' : '#1e293b'}
                                />
                                <text
                                    x={12}
                                    y={HEADER_HEIGHT / 2 + 5}
                                    fill="#ffffff"
                                    fontSize={13}
                                    fontWeight="bold"
                                    style={{ userSelect: 'none' }}
                                    onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTableId(table.id);
                                        setColumnDrawerOpen(true);
                                    }}
                                >
                                    {table.name}
                                </text>

                                {/* Icona d'edició ràpida a la capçalera */}
                                {!readOnly && (
                                    <g
                                        transform={`translate(${dims.width - 24}, ${HEADER_HEIGHT / 2 - 8})`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTableId(table.id);
                                            setColumnDrawerOpen(true);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <rect width={16} height={16} rx={3} fill={isSelected ? '#0958d9' : '#334155'} />
                                        <text x={8} y={12} textAnchor="middle" fontSize={10} fill="#ffffff">⚙️</text>
                                    </g>
                                )}

                                {/* Files de columnes */}
                                {table.columns.map((col, idx) => {
                                    const y = HEADER_HEIGHT + idx * ROW_HEIGHT;
                                    const isFkActive = fkSource?.table === table.name && fkSource?.column === col.name;

                                    return (
                                        <g key={col.id} transform={`translate(0, ${y})`}>
                                            {/* Línia separadora */}
                                            <line x1={0} y1={0} x2={dims.width} y2={0} stroke="#f1f5f9" strokeWidth={1} />

                                            {/* Icona PK o FK */}
                                            {col.isPK ? (
                                                <text x={8} y={ROW_HEIGHT / 2 + 4} fontSize={12} fill="#d97706" fontWeight="bold">🔑</text>
                                            ) : (
                                                <circle cx={14} cy={ROW_HEIGHT / 2} r={3} fill="#94a3b8" />
                                            )}

                                            {/* Nom de la columna */}
                                            <text
                                                x={28}
                                                y={ROW_HEIGHT / 2 + 4}
                                                fontSize={12}
                                                fontWeight={col.isPK ? 'bold' : 'normal'}
                                                fill={col.isPK ? '#0f172a' : '#334155'}
                                                style={{ userSelect: 'none' }}
                                            >
                                                {col.name}
                                            </text>

                                            {/* Tipus de dades */}
                                            <text
                                                x={dims.width - 32}
                                                y={ROW_HEIGHT / 2 + 4}
                                                fontSize={10}
                                                fill="#64748b"
                                                textAnchor="end"
                                                style={{ userSelect: 'none', fontFamily: 'monospace' }}
                                            >
                                                {col.type}
                                            </text>

                                            {/* Botó enllaçar FK */}
                                            {!readOnly && (
                                                <g
                                                    transform={`translate(${dims.width - 24}, ${ROW_HEIGHT / 2 - 8})`}
                                                    onClick={(e) => handleColumnClickForFk(table.name, col.name, e)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <rect width={16} height={16} rx={3} fill={isFkActive ? '#1890ff' : '#f1f5f9'} />
                                                    <text x={8} y={12} textAnchor="middle" fontSize={10} fill={isFkActive ? '#fff' : '#475569'}>🔗</text>
                                                </g>
                                            )}
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>

            {/* Panell Lateral Drawer per a Edició de Columnes */}
            <Drawer
                title={
                    <Space>
                        <SettingOutlined style={{ color: '#1890ff' }} />
                        <span>Editar Columnes de la Taula: <strong>{selectedTable?.name}</strong></span>
                    </Space>
                }
                placement="right"
                width={560}
                open={columnDrawerOpen && !!selectedTable}
                onClose={() => setColumnDrawerOpen(false)}
                extra={
                    selectedTable && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleAddColumn(selectedTable.id)}
                        >
                            Afegir Columna
                        </Button>
                    )
                }
            >
                {selectedTable && (
                    <div>
                        <div style={{ marginBottom: 14 }}>
                            <Text strong style={{ fontSize: 13 }}>Nom de la Taula (SQL):</Text>
                            <Input
                                style={{ marginTop: 4, fontWeight: 700 }}
                                value={selectedTable.name}
                                onChange={e => {
                                    const val = e.target.value.toUpperCase();
                                    setDiagram(prev => ({
                                        ...prev,
                                        tables: prev.tables.map(t => t.id === selectedTable.id ? { ...t, name: val } : t)
                                    }));
                                }}
                            />
                        </div>

                        <Divider style={{ margin: '14px 0' }} />

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <Text strong style={{ fontSize: 13 }}>Llista de Columnes:</Text>
                            <Tag color="blue">{selectedTable.columns.length} columnes</Tag>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {selectedTable.columns.map((col, idx) => (
                                <Card
                                    key={col.id}
                                    size="small"
                                    style={{
                                        borderLeft: col.isPK ? '3px solid #d97706' : '3px solid #cbd5e1',
                                        background: col.isPK ? '#fffbeb' : '#ffffff'
                                    }}
                                    bodyStyle={{ padding: 10 }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Tooltip title={col.isPK ? "Clau Primària (PK) activada" : "Marcar com a Clau Primària (PK)"}>
                                            <Button
                                                size="small"
                                                type={col.isPK ? "primary" : "default"}
                                                icon={<KeyOutlined />}
                                                onClick={() => toggleColumnPk(selectedTable.id, col.id)}
                                                style={{
                                                    background: col.isPK ? '#d97706' : undefined,
                                                    borderColor: col.isPK ? '#d97706' : undefined
                                                }}
                                            >
                                                PK
                                            </Button>
                                        </Tooltip>

                                        <Input
                                            size="small"
                                            value={col.name}
                                            placeholder="Nom columna"
                                            style={{ flex: 1, fontWeight: col.isPK ? 700 : 400 }}
                                            onChange={e => handleUpdateColumnName(selectedTable.id, col.id, e.target.value)}
                                        />

                                        <Select
                                            size="small"
                                            value={col.type}
                                            style={{ width: 140 }}
                                            onChange={val => handleUpdateColumnType(selectedTable.id, col.id, val)}
                                        >
                                            {SQL_TYPES.map(t => (
                                                <Option key={t} value={t}>{t}</Option>
                                            ))}
                                        </Select>

                                        <Tooltip title="Eliminar columna">
                                            <Button
                                                size="small"
                                                danger
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                disabled={selectedTable.columns.length <= 1}
                                                onClick={() => handleRemoveColumn(selectedTable.id, col.id)}
                                            />
                                        </Tooltip>
                                    </div>
                                </Card>
                            ))}
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <Button
                                block
                                icon={<PlusOutlined />}
                                onClick={() => handleAddColumn(selectedTable.id)}
                                style={{ borderStyle: 'dashed' }}
                            >
                                + Afegir una altra columna
                            </Button>
                        </div>
                    </div>
                )}
            </Drawer>

            {/* Modal Nova Taula */}
            <Modal
                title="Crear Nova Taula Relacional"
                open={newTableModalVisible}
                onCancel={() => setNewTableModalVisible(false)}
                onOk={handleAddTable}
                okText="Crear Taula"
                cancelText="Cancel·lar"
            >
                <div style={{ marginBottom: 8 }}>Nom de la taula (SQL):</div>
                <Input
                    placeholder="Ex: CLIENTS, COMANDES, PRODUCTES"
                    value={newTableName}
                    onChange={e => setNewTableName(e.target.value)}
                    onPressEnter={handleAddTable}
                    autoFocus
                />
            </Modal>

            {/* Modal Avaluació */}
            <Modal
                title={
                    <Space>
                        {evalResult?.isCorrect ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 22 }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 22 }} />
                        )}
                        <span style={{ fontWeight: 700 }}>
                            {evalResult?.isCorrect ? 'Model Relacional Correcte!' : 'Correcció del Model Relacional'}
                        </span>
                    </Space>
                }
                open={evalModalVisible}
                onCancel={() => setEvalModalVisible(false)}
                footer={[<Button key="close" type="primary" onClick={() => setEvalModalVisible(false)}>Tancar</Button>]}
                width={650}
            >
                {evalResult && (
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <Title level={2} style={{ margin: 0, color: evalResult.isCorrect ? '#52c41a' : '#f5222d' }}>
                                {evalResult.score} / 100
                            </Title>
                            <Text type="secondary">
                                {evalResult.penalitzacioTotal && evalResult.penalitzacioTotal > 0 ? (
                                    <span>
                                        Nota base: <strong>{evalResult.notaBase || 0} pts</strong> · Penalització: <strong style={{ color: '#f5222d' }}>-{evalResult.penalitzacioTotal} pts</strong> ({evalResult.intentsFallitsPrevis} intents)
                                    </span>
                                ) : (
                                    "Puntuació obtinguda en base a la rúbrica i tests de taules"
                                )}
                            </Text>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-around', background: '#fafafa', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>Taules</div>
                                <div style={{ color: '#1890ff' }}>{evalResult.summary.tablesMatched} / {evalResult.summary.tablesExpected}</div>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>Columnes</div>
                                <div style={{ color: '#1890ff' }}>{evalResult.summary.columnsMatched} / {evalResult.summary.columnsExpected}</div>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>Tipus de dades</div>
                                <div style={{ color: '#52c41a' }}>{evalResult.summary.typesMatched} / {evalResult.summary.columnsExpected}</div>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>Relacions (FK)</div>
                                <div style={{ color: '#722ed1' }}>{evalResult.summary.fksMatched} / {evalResult.summary.fksExpected}</div>
                            </div>
                        </div>

                        <Title level={5}>Detall de la Correcció:</Title>
                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                            {evalResult.feedback.map((f, idx) => (
                                <Alert
                                    key={idx}
                                    type={f.type}
                                    showIcon
                                    message={f.message}
                                    style={{ marginBottom: 6 }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RelationalDiagramIde;
